import { ComponentProps, MutableRefObject, useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { MeshBasicMaterial } from 'three';
import { Text, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { helveticaNeue } from '@/lib/fonts';
import square from '@/img/ps1-sq.png';
import circle from '@/img/ps1-circ.png';
import triangle from '@/img/ps1-tri.png';
import download from '@/img/cloud-download-icon.png';
import x from '@/img/ps1-x.png';
import { MAX_OPACITY, MIN_OPACITY, PULSE_PERIOD } from '@/lib/globals';
import selectionFocusSound from '@/sounds/ob-sfx-move-selection-focus.mp3';
import commitSound from '@/sounds/ob-sfx-commit.mp3';
import { oscillate } from '@/lib/mathUtils';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { ClickTarget } from '@/components/ClickTarget';

export type Icon = 'square' | 'triangle' | 'circle' | 'x' | 'download';

type TextProps = ComponentProps<typeof Text>;

const ICON_URLS = {
    square,
    circle,
    triangle,
    x,
    download,
};

interface Props {
    icon: Icon;
    label: string;
    action: () => Promise<void> | void;
    position?: [number, number, number];
    maxWidth?: number;
    fontSize?: number;
    textPosition?: [number, number, number];
    clickableAreaDimensions: [number, number];
    siblingRef?: MutableRefObject<MeshBasicMaterial>;
}

export const PlaystationButton: VoidFunctionComponent<Props> = ({
    icon,
    label,
    action,
    position = [0, 0, 0],
    maxWidth = 3.5,
    fontSize = 0.28,
    textPosition = [0.5, 0.14, 0],
    clickableAreaDimensions,
    siblingRef,
}) => {
    const [playSelectionFocusSound] = useOnlybotsSound(selectionFocusSound);
    const iconMap = useTexture(ICON_URLS[icon]);
    const materialRef = useRef<MeshBasicMaterial>(new MeshBasicMaterial());
    const textRef = useRef<TextProps>();
    const timer = useRef<number>(0);
    const [hovered, setHovered] = useState<boolean>(false);
    const [playCommitSound] = useOnlybotsSound(commitSound);
    const over = (): void => {
        setHovered(true);
        playSelectionFocusSound();
    };
    const out = (): void => {
        setHovered(false);
        timer.current = 0;
    };
    useEffect(() => {
        if (hovered) {
            document.body.style.cursor = 'pointer';
        }
        return () => {
            document.body.style.cursor = 'auto';
        };
    }, [hovered]);

    useFrame((_, delta) => {
        if (hovered && textRef.current && materialRef.current) {
            timer.current += delta;
            materialRef.current.opacity = oscillate(MIN_OPACITY, MAX_OPACITY, timer.current, PULSE_PERIOD);
            textRef.current.fillOpacity = oscillate(MIN_OPACITY, MAX_OPACITY, timer.current, PULSE_PERIOD);
            if (siblingRef && siblingRef.current) {
                siblingRef.current.opacity = oscillate(MIN_OPACITY, MAX_OPACITY, timer.current, PULSE_PERIOD);
            }
        }

        if (!hovered && textRef.current && (textRef.current.fillOpacity !== 0.6 || materialRef.current.opacity !== 1)) {
            textRef.current.fillOpacity = 0.6;
            materialRef.current.opacity = 0.8;
            if (siblingRef && siblingRef.current) {
                siblingRef.current.opacity = 0.6;
            }
        }
    });

    return (
        <group
            position={position}
            onPointerOver={over}
            onPointerOut={out}
            onClick={(e) => {
                e.stopPropagation();
                playCommitSound();
                void action();
            }}
        >
            <ClickTarget width={clickableAreaDimensions[0]} height={clickableAreaDimensions[1]} center>
                <mesh position={[0, 0.035, 0]}>
                    <planeGeometry args={[0.59, 0.59]} />
                    <meshBasicMaterial ref={materialRef} map={iconMap} transparent />
                </mesh>
                <Text
                    ref={textRef}
                    font={helveticaNeue}
                    position={textPosition}
                    anchorX="left"
                    anchorY="top"
                    maxWidth={maxWidth}
                    fontSize={fontSize}
                    fillOpacity={0.6}
                >
                    {label}
                </Text>
            </ClickTarget>
        </group>
    );
};
